// Live totals in the job editor. Must match backend/app/services/expenses.py:entry_amounts.
import type { DayFx, EntryData, JobRates, YearRates } from "../api/types";

export function ratesFor(rates: Record<string, YearRates>, date: string): YearRates | undefined {
  const y = (date || "").slice(0, 4);
  if (rates[y]) return rates[y];
  const years = Object.keys(rates).sort();
  const prev = years.filter((k) => k <= y).pop() ?? years[0];
  return prev ? rates[prev] : undefined;
}

/**
 * The exchange rate for a day: its own if the server sent one, else the nearest earlier day's
 * (or the nearest later). Used for days added in the editor; the server fetches the real rate on save.
 */
export function fxFor(fx: Record<string, DayFx>, date: string): DayFx | undefined {
  if (fx[date]) return fx[date];
  const days = Object.keys(fx).sort();
  const near = days.filter((d) => d <= date).pop() ?? days[0];
  return near ? { ...fx[near], exact: false } : undefined;
}

// A day's meal allowance and HMRC tickboxes, with the labels used in the job editor and the jobs list.
export const MEALS = ["m5", "m10", "m15", "mu15"] as const;
export const MEAL_LABELS: Record<MealKey, string> = { m5: ">5H", m10: ">10H", m15: ">15H", mu15: "<15H" };
export const CHECKS = ["h_lunch", "h_dinner", "h_drink", "h_hotel"] as const;
export const CHECK_LABELS: Record<CheckKey, string> = {
  h_lunch: "Lunch",
  h_dinner: "Dinner",
  h_drink: "Drinks",
  h_hotel: "Hotel to office",
};
export type MealKey = (typeof MEALS)[number];
export type CheckKey = (typeof CHECKS)[number];

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * `perGbp` is the day's exchange rate (units of the job's HMRC currency per £1). If the job's
 * HMRC rates are in another currency and there's no rate, the HMRC items count as 0.
 */
export function entryAmounts(
  e: EntryData,
  job: JobRates & { hmrc_currency?: string | null },
  r: YearRates | undefined,
  perGbp?: number | null,
) {
  if (!r) return { travel: 0, meals: 0, hotel: 0, extra: 0, total: 0 };
  let hmrc =
    e.h_drink * job.drink_rate +
    e.h_lunch * job.lunch_rate +
    e.h_dinner * job.dinner_rate +
    e.h_hotel * job.hotel_rate;
  if (job.hmrc_currency) hmrc = perGbp ? hmrc / perGbp : 0;
  const travel = e.mileage * r.mileage_rate + e.train + e.taxi + e.parking;
  const meals = e.m5 * r.meal_5h + e.m10 * r.meal_10h + e.m15 * r.meal_15h + e.mu15 * r.meal_u15h + hmrc;
  const hotel = (e.hotel_nights ?? 0) * (r.hotel_night_rate ?? 0);
  return {
    travel: round2(travel),
    meals: round2(meals),
    hotel: round2(hotel),
    extra: round2(e.extra),
    total: round2(travel + meals + hotel + e.extra),
  };
}
